import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { problemsApi } from '../api/problemsApi';
import { thirdLevelBidApi } from '../api/thirdLevelBidApi';
import type { ThirdLevelSubMode, ThirdLevelEditorCategory } from '../types/thirdLevelBid';
import { useAuth } from './useAuth';
import { configApi } from '../api/configApi';
import type { Problem, AnswerResult, SessionSummary, SessionOrigin, SessionFeatureKey, StartSessionRequest, DifficultyCategory, DifficultyRange, PersistedSessionState } from '../types/problems';
import { DIFFICULTY_CATEGORIES } from '../types/problems';
import { saveSession, loadSession, clearSession } from '../utils/storage';
import { nextStreak, bestStreakSoFar } from '../utils/streak';

// Feature 026: the two re-drillable modes that store a server-side resume position. Maps a
// session origin to its feature key, or null for every other mode (which has no resume position).
function resumeFeatureKeyOf(origin: SessionOrigin | null): SessionFeatureKey | null {
  if (origin === 'important-sequences') return 'important-sequences';
  if (origin === 'review-problems') return 'review-problems';
  return null;
}

/**
 * What a problem looked like once it had been dealt with, kept so "Previous" can put it back on
 * screen exactly as the learner left it (client request, 2026-08-03).
 *
 * `results` cannot serve this: it is an append-only tally keyed by nothing, and it drops the two
 * things the verdict panel needs — the expert's own words and the bid the learner actually made.
 * Keyed by problem id rather than by index because `updateDifficultyRange` re-filters the set
 * underneath the index, and a record must survive that.
 */
export interface AnswerRecord {
  /** The graded verdict, or null when the problem was skipped rather than answered. */
  result: AnswerResult | null;
  userBid: string | null;
  skipped: boolean;
}

// T045-T046: Session state with original category and current range tracking.
// Exported for `viewProblemAt`'s test — nothing else outside this file uses it, and the hook's
// return value stays the public surface.
export interface SessionState {
  problems: Problem[];
  allProblems: Problem[]; // T050: Keep all problems for filtering when range changes
  currentIndex: number;
  score: { correct: number; incorrect: number; skipped: number };
  results: Array<{
    problemId: number;
    difficultyLevel: number | null; // null for a 3rd-level-bid tournament hand (the overcalling page is problems-only since feature 033)
    isCorrect: boolean | null;
    correctBid?: string;
    expertComment?: string;
  }>;
  isLoading: boolean;
  error: string | null;
  lastResult: AnswerResult | null;
  showingResult: boolean;
  sessionOrigin: SessionOrigin | null;
  // T045: Track original category for mid-session slider bounds
  originalCategory: DifficultyCategory | null;
  categoryBounds: { min: number; max: number } | null;
  // T046: Track current range (can differ from initial selection)
  currentRange: DifficultyRange | null;
  // T050: Track answered problem IDs for filtering
  answeredProblemIds: number[];
  // T051: Flag when no problems available in current range
  noProblemsInRange: boolean;
  // T064: Track user's last submitted bid for "You bid:" display
  lastUserBid: string | null;
  // Feature 023 — the CURRENT run, signed: positive for right answers in a row, negative for wrong
  // ones. It restarts at ±1 when the run turns, which is what distinguishes it from `score` (the
  // visit's running total, which accumulates through zero). A skip is not an answer and leaves both
  // the run and its best alone.
  streak: number;
  // The best run of the visit. Floors at 0 because it starts there and a losing run is never a
  // "best" — Math.max against a negative streak keeps the previous value.
  bestStreak: number;
  // Every problem dealt with this session, by id — what "Previous" replays. See AnswerRecord.
  answerRecords: Record<number, AnswerRecord>;
}

interface LocationState {
  problems?: Problem[];
  totalAvailable?: number;
  sessionOrigin?: SessionOrigin;
}

const initialState: SessionState = {
  problems: [],
  allProblems: [],
  currentIndex: 0,
  score: { correct: 0, incorrect: 0, skipped: 0 },
  results: [],
  isLoading: false,
  error: null,
  lastResult: null,
  showingResult: false,
  sessionOrigin: null,
  originalCategory: null,
  categoryBounds: null,
  currentRange: null,
  answeredProblemIds: [],
  noProblemsInRange: false,
  lastUserBid: null,
  streak: 0,
  bestStreak: 0,
  answerRecords: {},
};

/**
 * Move the session's view to `index`, restoring whatever that problem has already been through.
 *
 * The one place both "Previous" and "Next Problem" go through, so stepping either way lands on
 * the same picture of a problem: a graded one comes back with its verdict (and is therefore
 * beyond re-answering — submit and skip both refuse while `showingResult` is set), a skipped one
 * comes back marked skipped, and one that was never dealt with comes back open.
 */
export function viewProblemAt(prev: SessionState, index: number): SessionState {
  const target = prev.problems[index];
  const record = target ? prev.answerRecords[target.id] : undefined;
  return {
    ...prev,
    currentIndex: index,
    showingResult: !!record,
    lastResult: record?.result ?? null,
    lastUserBid: record?.userBid ?? null,
  };
}

export function useProblemSession() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { accessibleFeatures } = useAuth();
  // Editor mode (FR-027): the inline authoring controls are gated on the 'third_level_bid_editing'
  // feature grant (feature 011), not on role. Admins receive the full active catalog in
  // accessibleFeatures, so they always qualify; editors only when an Admin has assigned them this
  // feature. Public users never have it.
  const isThirdLevelEditor = accessibleFeatures.some(f => f.code === 'third_level_bid_editing');
  // The editor data-category filter (FR-028); default Unanswered. Only used for "3rd-level-bid".
  const [editorCategory, setEditorCategoryState] = useState<ThirdLevelEditorCategory>('Unanswered');
  // The active opener slice for "3rd-level-bid", retained so a category change / Refresh can
  // re-fetch the same slice.
  const thirdLevelSubModeRef = useRef<ThirdLevelSubMode | undefined>(undefined);
  const thirdLevelOpeningBidRef = useRef<string | undefined>(undefined);
  const [state, setState] = useState<SessionState>(initialState);
  const [initialized, setInitialized] = useState(false);
  // Feature 026: the continue-or-restart prompt shown when re-entering a re-drillable mode with
  // unfinished saved progress. Null when no prompt is pending. `savedPosition` is the number of
  // problems already dealt with (resume opens at index savedPosition, i.e. question savedPosition+1).
  const [resumePrompt, setResumePrompt] = useState<{ feature: SessionFeatureKey; savedPosition: number } | null>(null);
  // Always-fresh view of state for use inside zero-dependency callbacks (position writes need the
  // current index/length without widening those callbacks' dependency arrays).
  const stateRef = useRef(state);
  stateRef.current = state;
  // T056: Track if we should skip the next save (for session restore)
  const skipNextSave = useRef(false);
  // v1.4: Track if we've already attempted to start a mode-based session
  const modeSessionStarted = useRef(false);
  // Session 2026-06-08: the active "3rd-level-bid" opener slice (`<subMode>|<openingBid>`),
  // persisted alongside the session so a refresh restores only the matching slice.
  const thirdLevelSubModeKeyRef = useRef<string | undefined>(undefined);

  // Feature 026: write the resume position after a problem is dealt with. Fire-and-forget — a lost
  // write costs at most one repeated problem on the next resume, and must never block the flow.
  // `dealtCount` is the number of problems dealt with in the fixed order; `total` is the current
  // set size. When the whole set has been dealt with (or nothing remains) the stored position is
  // cleared so the next visit starts fresh with no prompt (FR-009). No-op for non-resumable modes.
  const persistResumePosition = useCallback((origin: SessionOrigin | null, dealtCount: number, total: number) => {
    const feature = resumeFeatureKeyOf(origin);
    if (!feature) return;
    if (dealtCount <= 0 || dealtCount >= total) {
      void problemsApi.clearSessionPosition(feature).catch(() => { /* best-effort */ });
    } else {
      void problemsApi.saveSessionPosition(feature, dealtCount).catch(() => { /* best-effort */ });
    }
  }, []);

  // Feature 026: on entering a re-drillable mode, fetch the saved position and decide whether to
  // prompt. Returns the position to resume from (>0 → show the continue-or-restart prompt), or 0
  // for "start at question 1, no prompt". A saved position that is no longer valid for the current
  // set (>= total, e.g. the review list shrank) is discarded and treated as 0 (FR-010). Fail-open:
  // any error yields 0 so a network blip never blocks practice.
  const resolveSavedPosition = useCallback(async (feature: SessionFeatureKey, total: number): Promise<number> => {
    try {
      const p = await problemsApi.getSessionPosition(feature);
      if (p > 0 && p < total) return p;
      if (p >= total) void problemsApi.clearSessionPosition(feature).catch(() => { /* best-effort */ });
      return 0;
    } catch {
      return 0;
    }
  }, []);

  // Helper to determine session origin from URL mode parameter
  const getUrlSessionOrigin = useCallback((): SessionOrigin | null => {
    const mode = searchParams.get('mode');
    if (mode === 'important-sequences') return 'important-sequences';
    if (mode === 'review') return 'review-problems';
    if (mode === 'overcalling') return 'overcalling';
    if (mode === '3rd-level-bid') return '3rd-level-bid';
    return null; // No URL mode = difficulty-selector or dashboard
  }, [searchParams]);

  // The opener-based slice key from the URL for the "3rd-level-bid" mode (Session 2026-06-08).
  const thirdLevelSubModeKeyFromUrl = useCallback((): string => {
    const subMode = searchParams.get('subMode') ?? '';
    const openingBid = searchParams.get('openingBid') ?? '';
    return `${subMode}|${openingBid}`;
  }, [searchParams]);

  // T057: Restore session from IndexedDB on mount. loadSession/clearSession are async
  // (IndexedDB); the auto-start effects below all gate on `initialized`, so they wait
  // until restore resolves before deciding whether to start a fresh session.
  useEffect(() => {
    if (initialized) return;

    const locationState = location.state as LocationState | null;
    const urlSessionOrigin = getUrlSessionOrigin();

    // Priority 1: Navigation state from Dashboard takes precedence (no storage read)
    if (locationState?.problems && locationState.problems.length > 0) {
      setState({
        ...initialState,
        problems: locationState.problems,
        sessionOrigin: locationState.sessionOrigin ?? null,
      });
      // Clear the location state to prevent re-initialization on refresh
      navigate(location.pathname, { replace: true, state: null });
      setInitialized(true);
      return;
    }

    // Priority 2: If ?serial= is present, skip restore — let serial auto-start handle it.
    // The ?id= dev-only 3rd-level-bid locator (feature 013, FR-018) follows the same
    // skip-restore contract — when an explicit URL locator is in play, the user wants that
    // specific item, not whatever was persisted. (The overcalling ?id= locator went with the
    // tournament hands in feature 033; the module-practice by-id locator replaces it.)
    if (searchParams.get('serial') || searchParams.get('id')) {
      void clearSession();
      setInitialized(true);
      return;
    }

    // Priority 3: Try to restore from IndexedDB (async)
    let cancelled = false;
    void (async () => {
      const persistedSession = await loadSession();
      if (cancelled) return;

      if (persistedSession && persistedSession.problems.length > 0) {
        // FIX: Check if the persisted session's mode matches the current URL mode
        // If they don't match, clear the old session and let a new one start
        const persistedOrigin = persistedSession.sessionOrigin;

        // Determine if modes match:
        // - If URL has ?mode=important-sequences, persisted must be 'important-sequences'
        // - If URL has ?mode=review, persisted must be 'review-problems'
        // - If URL has no mode, persisted must be 'difficulty-selector' or 'dashboard' or null
        const modesMatch = urlSessionOrigin === null
          ? (persistedOrigin === 'difficulty-selector' || persistedOrigin === 'dashboard' || persistedOrigin === null)
          : persistedOrigin === urlSessionOrigin;

        // For "3rd-level-bid", also require the persisted opener slice to match the URL slice,
        // so refreshing in a different sub-accordion starts fresh (Session 2026-06-08).
        const thirdLevelSliceMatches = urlSessionOrigin !== '3rd-level-bid'
          || persistedSession.thirdLevelSubModeKey === thirdLevelSubModeKeyFromUrl();

        if (modesMatch && thirdLevelSliceMatches) {
          // Keep the restored opener slice so later saves (after answering) preserve it.
          thirdLevelSubModeKeyRef.current = persistedSession.thirdLevelSubModeKey;
          skipNextSave.current = true; // Don't save what we just loaded
          setState({
            ...initialState,
            problems: persistedSession.problems,
            allProblems: persistedSession.allProblems,
            currentIndex: persistedSession.currentIndex,
            score: persistedSession.score,
            results: persistedSession.results,
            sessionOrigin: persistedSession.sessionOrigin,
            originalCategory: persistedSession.originalCategory,
            categoryBounds: persistedSession.categoryBounds,
            currentRange: persistedSession.currentRange,
            answeredProblemIds: persistedSession.answeredProblemIds,
            // Snapshots written before the streak existed carry neither field; default both to 0
            // so a restored session shows "Streak: 0", not "Streak: undefined".
            streak: persistedSession.streak ?? 0,
            bestStreak: persistedSession.bestStreak ?? 0,
            // Snapshots written before "Previous" existed carry no records; an empty map simply
            // means every earlier problem comes back open rather than replayed.
            answerRecords: persistedSession.answerRecords ?? {},
          });
        } else {
          // Modes don't match - clear old session so new mode can start fresh
          void clearSession();
        }
      }

      if (!cancelled) setInitialized(true);
    })();

    return () => { cancelled = true; };
  }, [location.state, location.pathname, navigate, initialized, getUrlSessionOrigin, searchParams, thirdLevelSubModeKeyFromUrl]);

  // T056: Save session to localStorage on state changes
  useEffect(() => {
    // Skip initial render and restore operations
    if (!initialized || skipNextSave.current) {
      skipNextSave.current = false;
      return;
    }

    // Feature 026: don't persist an in-flight snapshot while the continue-or-restart prompt is
    // unresolved — a snapshot saved at index 0 would otherwise be silently restored on a refresh
    // and bypass the prompt. Once the user chooses, saving resumes normally.
    if (resumePrompt) {
      return;
    }

    // Only save if we have an active session
    if (state.problems.length > 0 && !state.isLoading && !state.error) {
      const persistData: Omit<PersistedSessionState, 'savedAt'> = {
        problems: state.problems,
        allProblems: state.allProblems,
        currentIndex: state.currentIndex,
        score: state.score,
        results: state.results,
        sessionOrigin: state.sessionOrigin,
        originalCategory: state.originalCategory,
        categoryBounds: state.categoryBounds,
        currentRange: state.currentRange,
        answeredProblemIds: state.answeredProblemIds,
        streak: state.streak,
        bestStreak: state.bestStreak,
        answerRecords: state.answerRecords,
        thirdLevelSubModeKey: state.sessionOrigin === '3rd-level-bid'
          ? thirdLevelSubModeKeyRef.current
          : undefined,
      };
      void saveSession(persistData);
    }
  }, [initialized, state, resumePrompt]);

  const currentProblem = state.problems[state.currentIndex] ?? null;
  const isSessionComplete = state.currentIndex >= state.problems.length && state.problems.length > 0;
  const totalProblems = state.problems.length;

  /**
   * T019: Starts a difficulty-selector session from a minDifficulty/maxDifficulty range (the
   * only selection the session endpoint accepts since the 2026-10-06 cleanup removed the legacy
   * componentId and openingBids paths).
   * T045-T046: Also accepts optional category for mid-session range tracking
   *
   * FIX 2026-01-12: Always fetch full 1-10 range to enable mid-session slider expansion.
   * Filter to initially selected range client-side, but store all problems in allProblems.
   */
  const startSession = useCallback(async (request: StartSessionRequest, category?: DifficultyCategory) => {
    setState(prev => ({ ...prev, isLoading: true, error: null, noProblemsInRange: false }));

    try {
      // FIX 2026-01-12: Always fetch the full 1-10 range to allow mid-session slider
      // adjustment beyond original category bounds
      const response = await problemsApi.startSession({ minDifficulty: 1, maxDifficulty: 10 });

      if (response.problems.length === 0) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: 'No unseen problems available for this difficulty range',
        }));
        return;
      }

      // T045: Determine category bounds for mid-session slider
      const categoryConfig = category ? DIFFICULTY_CATEGORIES[category] : null;

      // FIX 2026-01-12: Filter to initially selected range, but keep all problems in allProblems
      const initialMin = request.minDifficulty;
      const initialMax = request.maxDifficulty;
      const filteredProblems = response.problems.filter(
        p => (p.difficultyLevel ?? -1) >= initialMin && (p.difficultyLevel ?? -1) <= initialMax);

      // Check if no problems in the initially selected range
      if (filteredProblems.length === 0) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: `No unseen problems available for difficulty range ${initialMin}-${initialMax}`,
        }));
        return;
      }

      setState({
        ...initialState,
        problems: filteredProblems,
        allProblems: response.problems, // FIX: Store ALL problems (1-10) for mid-session filtering
        isLoading: false,
        sessionOrigin: 'difficulty-selector',
        // T045: Track original category and bounds
        originalCategory: category ?? null,
        categoryBounds: categoryConfig ? { min: categoryConfig.min, max: categoryConfig.max } : null,
        // T046: Track current range
        currentRange: { minLevel: request.minDifficulty, maxLevel: request.maxDifficulty },
      });
    } catch (err) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to start session',
      }));
    }
  }, []);

  /**
   * T095: Start session for Important Sequences mode (v1.4)
   * v1.8 (FR-100, FR-101): Backend now returns ALL important sequence problems without
   * excluding history. Session restarts from beginning each time, no completion message.
   */
  const startImportantSequencesSession = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    setResumePrompt(null);

    try {
      const response = await problemsApi.getImportantSequences();

      // v1.8: If no important sequence problems exist at all (database empty),
      // show appropriate message. This is different from "all answered" scenario.
      if (response.problems.length === 0) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: 'No problems marked as Important Sequences are available.',
        }));
        return;
      }

      // Feature 026: decide whether to resume BEFORE the table is interactive.
      const savedPosition = await resolveSavedPosition('important-sequences', response.problems.length);

      setState({
        ...initialState,
        problems: response.problems,
        allProblems: response.problems,
        isLoading: false,
        sessionOrigin: 'important-sequences',
      });
      if (savedPosition > 0) {
        setResumePrompt({ feature: 'important-sequences', savedPosition });
      }
    } catch (err) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load important sequences',
      }));
    }
  }, [resolveSavedPosition]);

  /**
   * Start session for Overcalling mode.
   * Fetches problems where the opponent made the opening bid.
   * Like Important Sequences, returns all eligible problems without history exclusion.
   */
  const startOvercallingSession = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await problemsApi.getOvercalling();

      if (response.problems.length === 0) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: "No more unseen questions here. If you've been practicing, clear your problem history on the Account page to see them again.",
        }));
        return;
      }

      setState({
        ...initialState,
        problems: response.problems,
        allProblems: response.problems,
        isLoading: false,
        sessionOrigin: 'overcalling',
      });
    } catch (err) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load overcalling problems',
      }));
    }
  }, []);

  /**
   * Start session for "Decision to make a 3rd level bid" mode (feature 013). Isolated data
   * call to the feature's own endpoint; the items are Problem-shaped so the reused page renders
   * them unchanged. The server already orders by role, so the snapshot is stored as-is (no
   * client reshuffle, FR-010 / FR-019).
   */
  const startThirdLevelBidSession = useCallback(async (
    subMode?: ThirdLevelSubMode,
    openingBid?: string,
    category?: ThirdLevelEditorCategory,
  ) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    thirdLevelSubModeKeyRef.current = `${subMode ?? ''}|${openingBid ?? ''}`;
    thirdLevelSubModeRef.current = subMode;
    thirdLevelOpeningBidRef.current = openingBid;

    try {
      // Editor mode (FR-028): pass the data category so the server enriches + filters. The
      // server ignores `category` for public callers, so it is harmless when not an editor.
      const response = await thirdLevelBidApi.getCandidates(
        subMode, openingBid, isThirdLevelEditor ? (category ?? editorCategory) : undefined);

      if (response.problems.length === 0) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          problems: [],
          allProblems: [],
          sessionOrigin: '3rd-level-bid',
          error: "No more unseen questions here. If you've been practicing, clear your problem history on the Account page to see them again.",
        }));
        return;
      }

      setState({
        ...initialState,
        problems: response.problems,
        allProblems: response.problems,
        isLoading: false,
        sessionOrigin: '3rd-level-bid',
      });
    } catch (err) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load 3rd-level-bid decision problems',
      }));
    }
  }, [isThirdLevelEditor, editorCategory]);

  // Editor mode (FR-028): change the data category → re-materialise the same slice with the
  // new filter. (FR-029/FR-030 navigation happens in-memory; only category/Refresh re-fetch.)
  const setEditorCategory = useCallback((category: ThirdLevelEditorCategory) => {
    setEditorCategoryState(category);
    startThirdLevelBidSession(thirdLevelSubModeRef.current, thirdLevelOpeningBidRef.current, category);
  }, [startThirdLevelBidSession]);

  // Editor mode (FR-028): "Refresh" reloads the current category from the database.
  const refreshThirdLevelEditor = useCallback(() => {
    startThirdLevelBidSession(thirdLevelSubModeRef.current, thirdLevelOpeningBidRef.current, editorCategory);
  }, [startThirdLevelBidSession, editorCategory]);

  /**
   * T111: Start session for Review Problems mode (v1.4)
   */
  const startReviewSession = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    setResumePrompt(null);

    try {
      const response = await problemsApi.getReviewProblems();

      if (response.problems.length === 0) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: 'No problems in your review list',
        }));
        return;
      }

      // Extract Problem objects from ReviewProblem wrapper
      const problems = response.problems.map(rp => rp.problem);

      // Feature 026: decide whether to resume BEFORE the table is interactive.
      const savedPosition = await resolveSavedPosition('review-problems', problems.length);

      setState({
        ...initialState,
        problems,
        allProblems: problems,
        isLoading: false,
        sessionOrigin: 'review-problems',
      });
      if (savedPosition > 0) {
        setResumePrompt({ feature: 'review-problems', savedPosition });
      }
    } catch (err) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load review problems',
      }));
    }
  }, [resolveSavedPosition]);

  // Feature 026: the user chose "continue from where you left off" — jump to the saved position
  // (question savedPosition + 1) and dismiss the prompt.
  const continueFromSavedPosition = useCallback(() => {
    setResumePrompt(current => {
      if (current) {
        setState(prev => ({ ...prev, currentIndex: current.savedPosition, showingResult: false, lastResult: null }));
      }
      return null;
    });
  }, []);

  // Feature 026: the user chose "start from the beginning" — discard the saved position and open
  // at question 1 (FR-007).
  const restartFromBeginning = useCallback(() => {
    setResumePrompt(current => {
      if (current) {
        void problemsApi.clearSessionPosition(current.feature).catch(() => { /* best-effort */ });
        setState(prev => ({ ...prev, currentIndex: 0, showingResult: false, lastResult: null }));
      }
      return null;
    });
  }, []);

  /**
   * T114: Remove problem from review list and advance to next (v1.4)
   */
  const removeFromReviewAndNext = useCallback(async (problemId: number) => {
    try {
      await problemsApi.removeFromReview(problemId);

      // Feature 026: removing a review problem is "dealing with" it. The removed item leaves the
      // list entirely, so the saved position is the current index (count of items that REMAIN
      // before the resume point) against the shrunken total. persistResumePosition clears the
      // position when nothing remains before/after (FR-009/FR-010).
      const before = stateRef.current;
      persistResumePosition(before.sessionOrigin, before.currentIndex, before.problems.length - 1);

      setState(prev => {
        // Remove the problem from the list
        const newProblems = prev.problems.filter(p => p.id !== problemId);
        const newAllProblems = prev.allProblems.filter(p => p.id !== problemId);

        // Adjust current index if needed
        const newIndex = Math.min(prev.currentIndex, Math.max(0, newProblems.length - 1));

        return {
          ...prev,
          problems: newProblems,
          allProblems: newAllProblems,
          currentIndex: newIndex,
          showingResult: false,
          lastResult: null,
        };
      });
    } catch (err) {
      setState(prev => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Failed to remove from review',
      }));
    }
  }, [persistResumePosition]);

  /**
   * Start a serial search session (dev-only QA feature).
   * Fetches a single problem by serial number and creates a session with the given mode.
   */
  const startSerialSession = useCallback(async (serialNumber: number, mode: SessionOrigin) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await problemsApi.getProblemBySerial(serialNumber);

      if (response.problems.length === 0) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: `No problem found with serial number ${serialNumber}`,
        }));
        return;
      }

      // For difficulty-selector mode, set slider state so the difficulty slider shows
      // (pinned to the problem's level, disabled — for QA layout testing)
      // difficulty-selector mode only — problems always carry a difficulty here.
      // Fallback to 0 satisfies the nullability widening introduced for overcalling-mode
      // tournament-hand items, which never reach this branch.
      const diffLevel = response.problems[0]!.difficultyLevel ?? 0;
      const showSlider = mode === 'difficulty-selector';

      setState({
        ...initialState,
        problems: response.problems,
        allProblems: response.problems,
        isLoading: false,
        sessionOrigin: mode,
        ...(showSlider && {
          originalCategory: 'beginner',
          categoryBounds: { min: 1, max: 10 },
          currentRange: { minLevel: diffLevel, maxLevel: diffLevel },
        }),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load problem by serial number';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
    }
  }, []);

  // v1.4: Auto-start session based on URL mode parameter
  // Track the last mode we started to detect mode changes
  const lastStartedMode = useRef<string | null>(null);

  useEffect(() => {
    if (!initialized) return;

    // Skip mode-based auto-start if a URL locator is active. ?serial= is handled by
    // the serial effect; ?id= (3rd-level-bid tournament-hand locator, feature 013) is
    // handled by the by-id effect below the serial effect.
    if (searchParams.get('serial')) return;
    if (searchParams.get('id') && searchParams.get('mode') === '3rd-level-bid') return;

    const mode = searchParams.get('mode');

    // For "3rd-level-bid" the slice key (subMode/openingBid) is part of the mode identity, so
    // switching sub-accordions is treated as a mode change and starts a fresh session
    // (Session 2026-06-08).
    const modeKey = mode === '3rd-level-bid'
      ? `${mode}|${thirdLevelSubModeKeyFromUrl()}`
      : mode;

    // Detect mode change and reset session if needed
    if (lastStartedMode.current !== modeKey) {
      // Mode has changed - always reset session state to allow new mode to start fresh
      // FIX: Reset state even when problems.length === 0, because sessionOrigin/error may still be set
      // This ensures navigating from "No problems to review" page to "Level of Difficulty" works correctly
      if (lastStartedMode.current !== null) {
        void clearSession();
        setState(initialState);
        setResumePrompt(null); // Feature 026: drop any pending prompt when the mode changes.
      }
      modeSessionStarted.current = false;
      lastStartedMode.current = modeKey;
    }

    // Don't start if already started for this mode or if we have problems
    if (modeSessionStarted.current || state.problems.length > 0) return;

    if (mode === 'important-sequences') {
      modeSessionStarted.current = true;
      startImportantSequencesSession();
    } else if (mode === 'review') {
      modeSessionStarted.current = true;
      startReviewSession();
    } else if (mode === 'overcalling') {
      modeSessionStarted.current = true;
      startOvercallingSession();
    } else if (mode === '3rd-level-bid') {
      modeSessionStarted.current = true;
      const subMode = (searchParams.get('subMode') ?? undefined) as ThirdLevelSubMode | undefined;
      const openingBid = searchParams.get('openingBid') ?? undefined;
      startThirdLevelBidSession(subMode, openingBid);
    }
  }, [initialized, searchParams, state.problems.length, startImportantSequencesSession, startReviewSession, startOvercallingSession, startThirdLevelBidSession, thirdLevelSubModeKeyFromUrl]);

  // Serial search auto-start: reads ?serial= param, checks config, starts serial session
  const serialSessionStarted = useRef(false);

  useEffect(() => {
    if (!initialized) return;

    const serialParam = searchParams.get('serial');
    if (!serialParam) return;
    if (serialSessionStarted.current) return;
    if (state.problems.length > 0) return;

    const serialNumber = parseInt(serialParam, 10);
    if (isNaN(serialNumber)) return;

    // Determine session origin from mode param
    const mode = searchParams.get('mode');
    let sessionOrigin: SessionOrigin = 'difficulty-selector';
    if (mode === 'important-sequences') sessionOrigin = 'important-sequences';
    else if (mode === 'review') sessionOrigin = 'review-problems';
    else if (mode === 'overcalling') sessionOrigin = 'overcalling';

    serialSessionStarted.current = true;

    // Check if serial search is enabled before fetching
    configApi.getClientConfig().then(config => {
      if (config.enableSerialSearch) {
        startSerialSession(serialNumber, sessionOrigin);
      } else {
        setState(prev => ({
          ...prev,
          error: 'Serial search is not enabled in this environment',
        }));
      }
    }).catch(() => {
      setState(prev => ({
        ...prev,
        error: 'Failed to check serial search configuration',
      }));
    });
  }, [initialized, searchParams, state.problems.length, startSerialSession]);

  // "Decision to make a 3rd level bid" tournament-hand-by-id locator (feature 013, FR-018):
  // dev-only (EnableSerialSearch flag), single-item session. (The overcalling page's own ?id=
  // locator was removed in feature 033 along with its tournament hands.)
  const thirdLevelHandSessionStarted = useRef(false);

  useEffect(() => {
    if (!initialized) return;

    const idParam = searchParams.get('id');
    if (!idParam) return;
    if (searchParams.get('mode') !== '3rd-level-bid') return;
    if (searchParams.get('serial')) return;
    if (thirdLevelHandSessionStarted.current) return;
    if (state.problems.length > 0) return;

    const tableResultId = parseInt(idParam, 10);
    if (isNaN(tableResultId)) return;

    thirdLevelHandSessionStarted.current = true;

    configApi.getClientConfig().then(config => {
      if (!config.enableSerialSearch) {
        setState(prev => ({
          ...prev,
          error: 'Serial search is not enabled in this environment',
        }));
        return;
      }
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      thirdLevelBidApi.getById(tableResultId)
        .then(response => {
          if (response.problems.length === 0) {
            setState(prev => ({
              ...prev,
              isLoading: false,
              error: `No 3rd-level-bid decision item found with id ${tableResultId}`,
            }));
            return;
          }
          setState({
            ...initialState,
            problems: response.problems,
            allProblems: response.problems,
            isLoading: false,
            sessionOrigin: '3rd-level-bid',
          });
        })
        .catch(err => {
          const status = (err as { response?: { status?: number } })?.response?.status;
          const message = status === 404
            ? `No 3rd-level-bid decision item found with id ${tableResultId}`
            : (err instanceof Error ? err.message : 'Failed to load item by id');
          setState(prev => ({
            ...prev,
            isLoading: false,
            error: message,
          }));
        });
    }).catch(() => {
      setState(prev => ({
        ...prev,
        error: 'Failed to check serial search configuration',
      }));
    });
  }, [initialized, searchParams, state.problems.length]);

  const submitAnswer = useCallback(async (bid: string) => {
    if (!currentProblem || state.showingResult) return;

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      // v1.8 (FR-102, FR-108): Pass sessionOrigin to API for conditional history recording.
      // Backend will skip recording to user_problem_history for important-sequences and review-problems.
      //
      // Feature 033: the overcalling page serves teaching problems only, so the feature 008 v2
      // `kind` discriminator (problem vs tournament hand) is no longer sent; the server still
      // accepts and ignores it.
      // Feature 013: isolated scoring endpoint (the id is a cardsgamehands tableresult id, not a
      // problems.id). No history is recorded server-side (FR-013).
      const result = state.sessionOrigin === '3rd-level-bid'
        ? await thirdLevelBidApi.submitAnswer(currentProblem.id, bid)
        : await problemsApi.submitAnswer(currentProblem.id, {
            bid,
            sessionOrigin: state.sessionOrigin ?? undefined,
          });

      // Editor mode (FR-028): record the editor's own answer so the hand flips to "Answered"
      // for this editor. Best-effort — a failure here must not block the scored feedback. This
      // is editor-authoring data, NOT learner history (FR-013).
      const recordEditorAnswer = state.sessionOrigin === '3rd-level-bid' && isThirdLevelEditor;
      if (recordEditorAnswer) {
        try {
          await thirdLevelBidApi.submitEditorAnswer(currentProblem.id, bid);
        } catch {
          // ignore — scoring/feedback still proceeds
        }
      }

      setState(prev => {
        // Editor mode: flip the current hand to "Answered" in memory (FR-028) so the status
        // label and the Answered/Unanswered category reflect it without a re-fetch. Also stash the
        // answer fields the server only sends for hands answered at fetch time (editorAnswer /
        // jeffBid / editorAnswerCorrect) so that navigating back (Previous) to a hand answered
        // THIS session renders the read-only review correctly instead of an empty result.
        const markAnswered = (p: Problem) =>
          recordEditorAnswer && p.id === currentProblem.id
            ? {
                ...p,
                isAnswered: true,
                editorAnswer: bid,
                jeffBid: result.correctBid,
                editorAnswerCorrect: result.isCorrect,
                // FR-034: stash Jeff's comment from the /answer response too, so navigating back
                // (Previous) to a hand answered THIS session shows "Jeff says:" in the review.
                jeffComment: result.expertComment,
              }
            : p;
        // Feature 023 — the signed run, by the shared rule. Computed inside the updater because
        // `bestStreak` is a function of the NEXT streak value, so both must come off one snapshot.
        const streak = nextStreak(prev.streak, result.isCorrect);
        return {
          ...prev,
          isLoading: false,
          lastResult: result,
          showingResult: true,
          // T064: Track user's submitted bid for "You bid:" display
          lastUserBid: bid,
          problems: prev.problems.map(markAnswered),
          allProblems: prev.allProblems.map(markAnswered),
          streak,
          bestStreak: bestStreakSoFar(prev.bestStreak, streak),
          score: {
            ...prev.score,
            correct: result.isCorrect ? prev.score.correct + 1 : prev.score.correct,
            incorrect: !result.isCorrect ? prev.score.incorrect + 1 : prev.score.incorrect,
          },
          results: [
            ...prev.results,
            {
              problemId: currentProblem.id,
              difficultyLevel: currentProblem.difficultyLevel,
              isCorrect: result.isCorrect,
              correctBid: result.correctBid,
              expertComment: result.expertComment,
            },
          ],
          // T050: Track answered problem ID for filtering
          answeredProblemIds: [...prev.answeredProblemIds, currentProblem.id],
          // What "Previous" replays for this problem.
          answerRecords: {
            ...prev.answerRecords,
            [currentProblem.id]: { result, userBid: bid, skipped: false },
          },
        };
      });

      // Feature 026: record the resume position after dealing with this problem. The problem just
      // answered is at the current index, so dealtCount = index + 1. Clears the position on the
      // final problem (FR-009). No-op for non-resumable modes.
      const dealt = stateRef.current;
      persistResumePosition(dealt.sessionOrigin, dealt.currentIndex + 1, dealt.problems.length);
    } catch (err) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to submit answer',
      }));
    }
  }, [currentProblem, state.showingResult, state.sessionOrigin, isThirdLevelEditor, persistResumePosition]);

  const skipProblem = useCallback(async () => {
    if (!currentProblem || state.showingResult) return;

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      // Feature 019 edition (2026-07-08): 3rd-level now records skips too, via its own endpoint, so
      // a skipped hand is suppressed like an answered one (closes the shipped-019 gap). Editing-
      // granted callers are a server-side no-op. All other origins use the shared problems skip.
      // (Feature 033: no `kind` — every overcalling item is a teaching problem now.)
      if (state.sessionOrigin === '3rd-level-bid') {
        await thirdLevelBidApi.skip(currentProblem.id);
      } else {
        await problemsApi.skipProblem(currentProblem.id, {
          sessionOrigin: state.sessionOrigin ?? undefined,
        });
      }

      setState(prev => ({
        ...prev,
        isLoading: false,
        showingResult: true,
        lastResult: null,
        score: {
          ...prev.score,
          skipped: prev.score.skipped + 1,
        },
        results: [
          ...prev.results,
          {
            problemId: currentProblem.id,
            difficultyLevel: currentProblem.difficultyLevel,
            isCorrect: null,
          },
        ],
        // T050: Track answered problem ID for filtering
        answeredProblemIds: [...prev.answeredProblemIds, currentProblem.id],
        // A skip is recorded too, so "Previous" brings the problem back marked skipped rather
        // than open — it has been dealt with, and re-answering it would score it twice.
        answerRecords: {
          ...prev.answerRecords,
          [currentProblem.id]: { result: null, userBid: null, skipped: true },
        },
      }));

      // Feature 026: a skip counts as dealing with the problem — same position write as an answer.
      const dealt = stateRef.current;
      persistResumePosition(dealt.sessionOrigin, dealt.currentIndex + 1, dealt.problems.length);
    } catch (err) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to skip problem',
      }));
    }
  }, [currentProblem, state.showingResult, state.sessionOrigin, persistResumePosition]);

  // v1.8: Removed revealAnswer function - review mode is now interactive like other modes

  // Forward through the set. It restores rather than clears, because once "Previous" exists the
  // problem ahead is not always a fresh one — a learner who stepped back two and came forward
  // again must find the verdicts they already earned, not blank pads offering to re-grade them.
  const nextProblem = useCallback(() => {
    setState(prev => viewProblemAt(prev, prev.currentIndex + 1));
  }, []);

  // Editor mode (FR-029): assign the level of difficulty to the current hand, then auto-advance
  // to the next hand. The assigned value is written into the in-memory item so revisiting it via
  // "Previous" (FR-030) shows the latest value instead of "N/A".
  const assignThirdLevelDifficulty = useCallback(async (level: number) => {
    const problem = state.problems[state.currentIndex];
    if (!problem) return;
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      await thirdLevelBidApi.assignDifficulty(problem.id, level);
      setState(prev => {
        const updateItem = (p: Problem) =>
          p.id === problem.id ? { ...p, difficultyLevel: level, isAssigned: true } : p;
        return {
          ...prev,
          isLoading: false,
          problems: prev.problems.map(updateItem),
          allProblems: prev.allProblems.map(updateItem),
          // Auto-advance to the next hand (FR-029).
          currentIndex: prev.currentIndex + 1,
          showingResult: false,
          lastResult: null,
          lastUserBid: null,
        };
      });
    } catch (err) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to assign difficulty',
      }));
    }
  }, [state.problems, state.currentIndex]);

  /**
   * Step back to the previous problem (inert at the start of the set).
   *
   * Editor mode's own control (FR-030) has always called this; since 2026-08-03 every learner
   * has it too, on every mode this page serves. Both land on the same read-only picture — see
   * `viewProblemAt` — so an editor stepping back through hands they assigned and a learner
   * stepping back through problems they answered see the one behaviour.
   */
  const goToPreviousProblem = useCallback(() => {
    setState(prev => viewProblemAt(prev, Math.max(0, prev.currentIndex - 1)));
  }, []);

  const getSessionSummary = useCallback((): SessionSummary => {
    const correctByDifficulty: Record<number, number> = {};

    state.results.forEach(result => {
      // Tournament-hand items have a null difficultyLevel — they are excluded from
      // the per-difficulty correctness breakdown (no synthetic bucket per spec A1).
      if (result.isCorrect === true && result.difficultyLevel !== null) {
        correctByDifficulty[result.difficultyLevel] =
          (correctByDifficulty[result.difficultyLevel] || 0) + 1;
      }
    });

    return {
      totalQuestions: state.results.length,
      answered: state.score.correct + state.score.incorrect,
      skipped: state.score.skipped,
      correct: state.score.correct,
      incorrect: state.score.incorrect,
      correctByDifficulty,
      bestStreak: state.bestStreak,
    };
  }, [state.results, state.score, state.bestStreak]);

  /**
   * Hand the visit's numbers to the scorecard and leave.
   *
   * `trail` is what the session called itself in the masthead the learner has been looking at —
   * ProblemsPage owns that string because only it knows the level a difficulty session was
   * started at ("Beginner · Bidding Practice"), which cannot be recovered from the origin alone.
   * Passing it through keeps the summary's trail identical to the banner above the table.
   *
   * `?from=problems` is not read by the page while there IS state; it exists so that a refresh
   * on the summary — which destroys location state — sends the learner back to `/problems`
   * instead of the module practice hub.
   */
  const navigateToSummary = useCallback((trail?: string) => {
    const summary = getSessionSummary();
    // T058: Clear the persisted session when it ends
    void clearSession();
    // sessionOrigin drives the "Start New Session" destination (FR-036, FR-110 to FR-114).
    navigate('/session-summary?from=problems', {
      state: {
        kind: 'problems',
        trail: trail ?? 'Bidding Practice',
        sessionOrigin: state.sessionOrigin,
        correctByDifficulty: summary.correctByDifficulty,
        totalQuestions: summary.totalQuestions,
        answered: summary.answered,
        correct: summary.correct,
        incorrect: summary.incorrect,
        skipped: summary.skipped,
        // The card's headline pair. The score is the net the status line has been showing all
        // session (the old summary called the same number "Final Score"); the streak is the run
        // the learner ended on, which is not the same as the best one and is reported beside it.
        score: summary.correct - summary.incorrect,
        streak: state.streak ?? 0,
        bestStreak: summary.bestStreak ?? 0,
      },
    });
  }, [navigate, getSessionSummary, state.sessionOrigin, state.streak]);

  const resetSession = useCallback(() => {
    // T058: Clear the persisted session on reset
    void clearSession();
    setState(initialState);
    setResumePrompt(null); // Feature 026
  }, []);

  /**
   * T047: Update difficulty range mid-session (FR-029)
   * T050: Filter remaining problems when range is adjusted
   * Changes take effect immediately without confirmation
   *
   * FIX 2026-01-12: Clear problems array when no problems in range,
   * and always update problems to reflect the new filtered set
   * FIX 2026-02-06: Reset showingResult when problem changes after range adjustment
   */
  const updateDifficultyRange = useCallback((minLevel: number, maxLevel: number) => {
    setState(prev => {
      // Filter all problems to new range, excluding answered ones
      const filteredProblems = prev.allProblems.filter(
        p => (p.difficultyLevel ?? -1) >= minLevel &&
             (p.difficultyLevel ?? -1) <= maxLevel &&
             !prev.answeredProblemIds.includes(p.id)
      );

      // T051: Check if no problems available in new range
      if (filteredProblems.length === 0) {
        return {
          ...prev,
          problems: [], // Clear problems so no problem is displayed
          currentIndex: 0,
          currentRange: { minLevel, maxLevel },
          noProblemsInRange: true,
          // Reset result state when no problems
          showingResult: false,
          lastResult: null,
          lastUserBid: null,
        };
      }

      // Check if the current problem will change
      const currentProblemId = prev.problems[prev.currentIndex]?.id;
      const newFirstProblemId = filteredProblems[0]?.id;
      const problemChanged = currentProblemId !== newFirstProblemId;

      return {
        ...prev,
        problems: filteredProblems,
        currentIndex: 0, // Reset to first problem in new filtered set
        currentRange: { minLevel, maxLevel },
        noProblemsInRange: false,
        // Reset result state only if the problem changed
        ...(problemChanged && {
          showingResult: false,
          lastResult: null,
          lastUserBid: null,
        }),
      };
    });
  }, []);

  // T025: Compute net score (correct - incorrect) for display
  const netScore = state.score.correct - state.score.incorrect;

  return {
    // State
    currentProblem,
    currentIndex: state.currentIndex,
    totalProblems,
    score: state.score,
    netScore, // T025: Net score for display
    // Feature 023 — the current run and the visit's best; see SessionState for the semantics.
    streak: state.streak,
    bestStreak: state.bestStreak,
    isLoading: state.isLoading,
    error: state.error,
    lastResult: state.lastResult,
    showingResult: state.showingResult,
    isSessionComplete,
    // T045-T046: Mid-session range adjustment state
    originalCategory: state.originalCategory,
    categoryBounds: state.categoryBounds,
    currentRange: state.currentRange,
    // T051: No problems in range flag
    noProblemsInRange: state.noProblemsInRange,
    // T064: User's last submitted bid for "You bid:" display
    lastUserBid: state.lastUserBid,
    // v1.4: Session origin for mode-specific UI behavior
    sessionOrigin: state.sessionOrigin,
    // Feature 026: continue-or-restart prompt state (null when no prompt is pending)
    resumePrompt,

    // Actions
    startSession,
    submitAnswer,
    skipProblem,
    nextProblem,
    getSessionSummary,
    navigateToSummary,
    resetSession,
    // T047: Mid-session range adjustment
    updateDifficultyRange,
    // v1.4: Mode-specific session actions
    startImportantSequencesSession,
    startReviewSession,
    startOvercallingSession,
    startThirdLevelBidSession,
    removeFromReviewAndNext,
    // Feature 026: resume prompt actions
    continueFromSavedPosition,
    restartFromBeginning,
    // Dev-only QA: Serial search
    startSerialSession,
    // Feature 013 editor mode (Session 2026-06-15, FR-027–FR-031)
    isThirdLevelEditor,
    editorCategory,
    setEditorCategory,
    refreshThirdLevelEditor,
    assignThirdLevelDifficulty,
    goToPreviousProblem,
  };
}
